import React, { FC, ReactNode, useMemo } from 'react'
import { Button } from 'antd'
import { LinkOutlined, PhoneOutlined, MailOutlined, InboxOutlined } from '@ant-design/icons'
import type { STREntityResponse } from '../types'
import { getBusinessSourceIcon } from '../utils'

const SECTION_GAP = 24
const SECTION_TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'Roboto',
  fontWeight: 500,
  fontSize: 14,
  color: '#000000d9',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 12,
}
const ICON_STYLE: React.CSSProperties = { fontSize: 16, color: '#595959' }
const PLATFORM_ICON_STYLE: React.CSSProperties = { width: 18, height: 18 }
const NO_DATA_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
  border: '1px dashed #d9d9d9',
  borderRadius: 6,
  color: '#bfbfbf',
  fontSize: 13,
}

const NoData: FC<{ text: string }> = ({ text }) => (
  <div style={NO_DATA_STYLE}>
    <InboxOutlined style={{ fontSize: 16 }} />
    {text}
  </div>
)
const BUTTON_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  textDecoration: 'none',
}

interface WeblinkItem {
  platform: string
  url: string
}

const flattenWeblinks = (weblinks: Record<string, string[]> | undefined): WeblinkItem[] => {
  if (!weblinks) return []
  return Object.entries(weblinks).flatMap(([platform, urls]) => urls.map(url => ({ platform, url })))
}

const WebLinksSection: FC<{ weblinks: Record<string, string[]> | undefined }> = ({ weblinks }) => {
  const items = useMemo(() => flattenWeblinks(weblinks), [weblinks])

  return (
    <div>
      <div style={SECTION_TITLE_STYLE}>
        <LinkOutlined style={ICON_STYLE} />
        Web Links
      </div>
      {items.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {items.map((item, idx) => {
            const iconSrc = getBusinessSourceIcon(item.platform)
            return (
              <Button
                key={`${item.platform}-${idx}`}
                href={item.url}
                target='_blank'
                rel='noreferrer'
                icon={
                  iconSrc ? <img src={iconSrc} alt={item.platform} style={PLATFORM_ICON_STYLE} /> : <LinkOutlined />
                }
                size='large'
                style={BUTTON_STYLE}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.url}</span>
              </Button>
            )
          })}
        </div>
      ) : (
        <NoData text='No web link data available' />
      )}
    </div>
  )
}

interface SimpleSectionConfig {
  icon: ReactNode
  title: string
  emptyText: string
  items: string[]
  getHref?: (item: string) => string
}

const SimpleSection: FC<SimpleSectionConfig> = ({ icon, title, emptyText, items, getHref }) => (
  <div>
    <div style={SECTION_TITLE_STYLE}>
      {icon}
      {title}
    </div>
    {items.length > 0 ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((item, idx) => (
          <Button key={idx} href={getHref?.(item)} size='large' style={BUTTON_STYLE}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item}</span>
          </Button>
        ))}
      </div>
    ) : (
      <NoData text={emptyText} />
    )}
  </div>
)

const SIMPLE_SECTIONS: Omit<SimpleSectionConfig, 'items'>[] = [
  {
    icon: <PhoneOutlined style={ICON_STYLE} />,
    title: 'Phones',
    emptyText: 'No phone data available',
    getHref: (phone: string) => `tel:${phone}`,
  },
  {
    icon: <MailOutlined style={ICON_STYLE} />,
    title: 'Emails',
    emptyText: 'No email data available',
    getHref: (email: string) => `mailto:${email}`,
  },
  {
    icon: <MailOutlined style={ICON_STYLE} />,
    title: 'Mailing Addresses',
    emptyText: 'No address data available',
  },
]

const ITEMS_KEY_MAP: Record<string, keyof Pick<STREntityResponse['contactInfo'], 'phones' | 'emails' | 'addresses'>> = {
  Phones: 'phones',
  Emails: 'emails',
  'Mailing Addresses': 'addresses',
}

interface ContactInfoProps {
  contactInfo: STREntityResponse['contactInfo']
}

const ContactInfo: FC<ContactInfoProps> = ({ contactInfo }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: SECTION_GAP, padding: '16px 0' }}>
    <WebLinksSection weblinks={contactInfo?.weblinks} />
    {SIMPLE_SECTIONS.map(section => (
      <SimpleSection key={section.title} {...section} items={contactInfo?.[ITEMS_KEY_MAP[section.title]] ?? []} />
    ))}
  </div>
)

export default ContactInfo
